import React, { useMemo } from "react";
import { Icon, Stack, Text } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

// ---- Color Config ----

const ENGAGEMENT_COLORS: Record<string, { bg: string; title: string; divider: string; totalBg: string; border: string }> = {
  MDO: { bg: "#EAF3FB", title: "#1252A3", divider: "#C9DBEE", totalBg: "#1252A3", border: "#C9DBEE" },
  MDE: { bg: "#ECF6EA", title: "#107C10", divider: "#C7DECC", totalBg: "#107C10", border: "#C7DECC" },
  MDI: { bg: "#F3EEF8", title: "#5C2D91", divider: "#D9C8EF", totalBg: "#5C2D91", border: "#D9C8EF" },
  MDA: { bg: "#FDE8E8", title: "#A4262C", divider: "#F0C0C0", totalBg: "#A4262C", border: "#F0C0C0" },
  Sentinel: { bg: "#EEF2FB", title: "#243A5E", divider: "#B8C8E8", totalBg: "#243A5E", border: "#B8C8E8" },
};

const DEFAULT_COLORS = { bg: "#F5F5F5", title: "#323130", divider: "#D9D9D9", totalBg: "#323130", border: "#D9D9D9" };

// ---- Helpers ----

const toText = (v: string | number): string => String(v ?? "").trim();

const parseNumber = (v: string | number | undefined): number | undefined => {
  if (v === undefined) return undefined;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : undefined;
};

const formatDecimal = (v: number | undefined, digits = 1): string =>
  v === undefined
    ? "-"
    : v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits });

/** Extract text inside outermost parens at end of label and pretty-format it. */
const extractLabelFormula = (label: string): string => {
  const first = label.indexOf("(");
  const last  = label.lastIndexOf(")");
  if (first === -1 || last <= first) return "";
  return label
    .substring(first + 1, last)
    .replace(/\bx\b/g, "×")
    .replace(/sqrt\(/g, "√(");
};

// ---- Data Structures ----

interface StandardEngagementData {
  baseScore?: number;
  activeDaysScore?: number;
  activeDaysFormula: string;
  deployScore?: number;
  deployRate?: number;
  totalScore?: number;
  activeDays?: number;
  formulaText: string;
}

interface SentinelEngagementData {
  fixedBase?: number;
  deployBonus?: number;
  totalScore?: number;
  formulaText: string;
}

// ---- Parsers ----

const buildStandardData = (rows: RawIndicatorRow[]): StandardEngagementData => {
  const result: StandardEngagementData = { activeDaysFormula: "", formulaText: "" };
  for (const row of rows) {
    const label = (row.factorLabel || "").trim();
    const val   = parseNumber(row.value);
    const lower = label.toLowerCase();

    if (lower.startsWith("base score")) {
      result.baseScore = val;
    } else if (lower.startsWith("active days score")) {
      result.activeDaysScore = val;
      result.activeDaysFormula = extractLabelFormula(label);
    } else if (lower.startsWith("deployment score")) {
      result.deployScore = val;
      if (val !== undefined) result.deployRate = val / 0.25;
    } else if (lower === "total engagement score") {
      result.totalScore = val;
    } else if (lower === "active days") {
      result.activeDays = val;
    } else if (lower === "formula:") {
      result.formulaText = toText(row.value);
    }
  }
  return result;
};

const buildSentinelData = (rows: RawIndicatorRow[]): SentinelEngagementData => {
  const result: SentinelEngagementData = { formulaText: "" };
  for (const row of rows) {
    const label = (row.factorLabel || "").trim();
    const val   = parseNumber(row.value);
    const lower = label.toLowerCase();

    if (lower.startsWith("base score")) {
      result.fixedBase = val;
    } else if (lower.startsWith("deployment bonus")) {
      result.deployBonus = val;
    } else if (lower === "total engagement score") {
      result.totalScore = val;
    } else if (lower === "formula:") {
      result.formulaText = toText(row.value);
    }
  }
  return result;
};

// ---- Shared sub-components ----

const Operator = ({ symbol, color }: { symbol: "+" | "="; color: string }) => (
  <Text
    variant="xLarge"
    styles={{
      root: {
        fontWeight: 700,
        color,
        flexShrink: 0,
        flexGrow: 0,
        alignSelf: "center",
        lineHeight: "1",
        paddingBottom: 4,
      },
    }}
  >
    {symbol}
  </Text>
);

interface EquationBoxProps {
  label: string;
  value: string;
  subText?: string;
  showCheck?: boolean;
  isTotal?: boolean;
  accentColor: string;
  totalBg?: string;
  wide?: boolean;
  medium?: boolean;
}

const EquationBox = ({
  label, value, subText, showCheck, isTotal, accentColor, totalBg, wide, medium,
}: EquationBoxProps) => (
  <Stack
    tokens={{ childrenGap: 3 }}
    styles={{
      root: {
        width: wide ? 120 : medium ? 96 : 72,
        flexShrink: 0,
        flexGrow: 0,
        backgroundColor: isTotal ? (totalBg || accentColor) : "#FFFFFF",
        border: `1px solid ${isTotal ? (totalBg || accentColor) : "#D9D9D9"}`,
        borderRadius: 6,
        padding: "10px 10px",
        alignItems: "center",
        justifyContent: "center",
      },
    }}
  >
    <Text
      variant="tiny"
      styles={{
        root: {
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontWeight: 600,
          color: isTotal ? "rgba(255,255,255,0.8)" : "#605E5C",
          textAlign: "center",
          lineHeight: "1.2",
        },
      }}
    >
      {label}
    </Text>
    <Text
      variant="xLarge"
      styles={{
        root: {
          fontWeight: 700,
          color: isTotal ? "#FFFFFF" : accentColor,
          lineHeight: "1.1",
        },
      }}
    >
      {value}
    </Text>
    {subText && (
      <Text
        variant="tiny"
        styles={{
          root: {
            color: isTotal ? "rgba(255,255,255,0.75)" : "#605E5C",
            textAlign: "center",
            lineHeight: "1.3",
          },
        }}
      >
        {subText}
      </Text>
    )}
    {showCheck && (
      <Icon
        iconName="CheckMark"
        styles={{ root: { color: accentColor, fontSize: 12, marginTop: 2 } }}
      />
    )}
  </Stack>
);

// ---- Standard Card (MDO / MDE / MDI / MDA) ----

const StandardEngagementCard = ({
  data, productAbbr, weight,
}: {
  data: StandardEngagementData; productAbbr: string; weight?: string;
}) => {
  const colors = ENGAGEMENT_COLORS[productAbbr] ?? DEFAULT_COLORS;

  const deployRateStr = data.deployRate !== undefined
    ? formatDecimal(data.deployRate, 1)
    : "-";

  const deploySubText = data.deployRate !== undefined
    ? `0.25×${formatDecimal(data.deployRate, 1)}`
    : "0.25×DeployRate";

  return (
    <Stack
      tokens={{ childrenGap: 14 }}
      styles={{
        root: {
          flex: "0 0 auto",
          minWidth: 0,
          maxWidth: 520,
          borderRadius: 10,
          padding: "16px 18px",
          border: `2px solid ${colors.border}`,
          backgroundColor: colors.bg,
        },
      }}
    >
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text
          variant="medium"
          styles={{
            root: {
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: colors.title,
            },
          }}
        >
          {productAbbr} Engagement
        </Text>
        {weight && (
          <Stack
            styles={{
              root: {
                backgroundColor: colors.title,
                borderRadius: 20,
                padding: "3px 12px",
              },
            }}
          >
            <Text variant="small" styles={{ root: { color: "#FFFFFF", fontWeight: 600 } }}>
              Weight: {weight}
            </Text>
          </Stack>
        )}
      </Stack>

      {/* Total Score */}
      <Text
        variant="xxLarge"
        styles={{ root: { fontWeight: 700, color: colors.title, lineHeight: "1" } }}
      >
        {formatDecimal(data.totalScore, 1)}
      </Text>

      {/* Equation row */}
      <Stack
        horizontal
        verticalAlign="stretch"
        tokens={{ childrenGap: 8 }}
        styles={{ root: { flexWrap: "nowrap" } }}
      >
        <EquationBox
          label="Base"
          value={formatDecimal(data.baseScore, 0)}
          subText="Engaged"
          showCheck
          accentColor={colors.title}
        />
        <Operator symbol="+" color={colors.title} />
        <EquationBox
          label="Active Days"
          value={formatDecimal(data.activeDaysScore, 1)}
          subText={data.activeDaysFormula || undefined}
          medium
          accentColor={colors.title}
        />
        <Operator symbol="+" color={colors.title} />
        <EquationBox
          label="Deployment"
          value={formatDecimal(data.deployScore, 1)}
          subText={deploySubText}
          medium
          accentColor={colors.title}
        />
        <Operator symbol="=" color={colors.title} />
        <EquationBox
          label="Total"
          value={formatDecimal(data.totalScore, 1)}
          isTotal
          accentColor={colors.title}
          totalBg={colors.totalBg}
        />
      </Stack>

      {/* Divider */}
      <div style={{ height: 1, backgroundColor: colors.divider }} />

      {/* Footer */}
      <Text variant="small" styles={{ root: { color: "#605E5C", fontStyle: "italic" } }}>
        ActiveDays: {data.activeDays ?? "-"} | DeployRate: {deployRateStr}%
      </Text>
    </Stack>
  );
};

// ---- Sentinel Card ----

const SentinelEngagementCard = ({
  data, weight,
}: {
  data: SentinelEngagementData; weight?: string;
}) => {
  const colors = ENGAGEMENT_COLORS.Sentinel;

  // Compute actual deploy rate: deployBonus = 0.3 × deployRate
  const deployRate = data.deployBonus !== undefined
    ? data.deployBonus / 0.3
    : undefined;

  const deployBonusSubText = deployRate !== undefined
    ? `0.3×${formatDecimal(deployRate, 1)} (DeployRate)`
    : "0.3×DeployRate";

  return (
    <Stack
      tokens={{ childrenGap: 14 }}
      styles={{
        root: {
          flex: "0 0 auto",
          minWidth: 0,
          maxWidth: 560,
          borderRadius: 10,
          padding: "16px 18px",
          border: `2px solid ${colors.border}`,
          backgroundColor: colors.bg,
        },
      }}
    >
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
          <Text
            variant="medium"
            styles={{
              root: {
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: colors.title,
              },
            }}
          >
            Sentinel Engagement
          </Text>
          {/* "Different Formula" badge */}
          <Stack
            styles={{
              root: {
                backgroundColor: "#FFF4CE",
                border: "1px solid #F2C94C",
                borderRadius: 4,
                padding: "2px 8px",
              },
            }}
          >
            <Text
              variant="tiny"
              styles={{
                root: {
                  color: "#7A5A00",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                },
              }}
            >
              Different Formula
            </Text>
          </Stack>
        </Stack>
        {weight && (
          <Stack
            styles={{
              root: {
                backgroundColor: colors.title,
                borderRadius: 20,
                padding: "3px 12px",
              },
            }}
          >
            <Text variant="small" styles={{ root: { color: "#FFFFFF", fontWeight: 600 } }}>
              Weight: {weight}
            </Text>
          </Stack>
        )}
      </Stack>

      {/* Total Score */}
      <Text
        variant="xxLarge"
        styles={{ root: { fontWeight: 700, color: colors.title, lineHeight: "1" } }}
      >
        {formatDecimal(data.totalScore, 1)}
      </Text>

      {/* Equation row */}
      <Stack
        horizontal
        verticalAlign="stretch"
        tokens={{ childrenGap: 8 }}
      >
        <EquationBox
          label="Fixed Base"
          value={formatDecimal(data.fixedBase, 0)}
          subText="All engaged Sentinel tenants"
          accentColor={colors.title}
          wide
        />
        <Operator symbol="+" color={colors.title} />
        <EquationBox
          label="Deployment Bonus"
          value={formatDecimal(data.deployBonus, 1)}
          subText={deployBonusSubText}
          accentColor={colors.title}
          wide
        />
        <Operator symbol="=" color={colors.title} />
        <EquationBox
          label="Total"
          value={formatDecimal(data.totalScore, 1)}
          isTotal
          accentColor={colors.title}
          totalBg={colors.totalBg}
        />
      </Stack>

      {/* Divider */}
      <div style={{ height: 1, backgroundColor: colors.divider }} />

      {/* Footer */}
      <Text variant="small" styles={{ root: { color: "#605E5C", fontStyle: "italic" } }}>
        {data.formulaText || "Formula: 70 + 0.3 × AdjustedDeploymentRate | No ActiveDays component"}
      </Text>
    </Stack>
  );
};

// ---- Main Export ----

interface MTPEngagementRawIndicatorCardsProps {
  rows: RawIndicatorRow[];
  productAbbr: string;
  weight?: string;
}

const MTPEngagementRawIndicatorCards: React.FC<MTPEngagementRawIndicatorCardsProps> = ({
  rows, productAbbr, weight,
}) => {
  const standardData = useMemo(
    () => productAbbr !== "Sentinel" ? buildStandardData(rows) : null,
    [rows, productAbbr]
  );
  const sentinelData = useMemo(
    () => productAbbr === "Sentinel" ? buildSentinelData(rows) : null,
    [rows, productAbbr]
  );

  if (productAbbr === "Sentinel" && sentinelData) {
    return <SentinelEngagementCard data={sentinelData} weight={weight} />;
  }
  if (standardData) {
    return <StandardEngagementCard data={standardData} productAbbr={productAbbr} weight={weight} />;
  }
  return null;
};

export default MTPEngagementRawIndicatorCards;
